import { EntityData, FormElement } from '../../lib/types'
import React, { useCallback, useEffect, useState } from 'react'
import ChangeIndicator from './change-indicator'
import { entities as entitiesApi } from '../../lib/api'
import Tag from '../tag'
import EntitySearch from '../entity-search'

type Props = {
    id: number
    element: FormElement
}

export default function MultiMagicPointerElement(props: Props) {
    const { id, element } = props
    const [loaded, setLoaded] = useState(false)
    const [text, setText] = useState('')
    const [entities, setEntities] = useState<Array<EntityData>>([])
    const [changed, setChanged] = useState(false)
    const [err, setErr] = useState('')

    // Load initial data
    useEffect(() => {
        if (loaded) return
        setLoaded(true)
        if (!id) return

        let promises = element.entities!.map((e) => entitiesApi.listMultiMP(id, element.target!, e))
        Promise.all(promises).then((allResults) => {
            allResults.forEach((results, i) => {
                results.forEach((e) => (e._entityIndex = i))
            })
            setEntities(allResults.reduce((a, b) => a.concat(b), []))
        })
    }, [loaded, id, element])

    const optionClick = useCallback(
        (entity: EntityData) => {
            setText('')
            setChanged(true)
            setErr('')

            let newEntities = entities.concat([entity])
            setEntities(newEntities)

            let entityArr = newEntities.map((e) => ({
                id: e.id,
                dataset: element.entities![e._entityIndex!].dataset,
            }))
            entitiesApi
                .updateMultiMP(id, element.target!, entityArr)
                .catch((err) => setErr(err.message || err))
        },
        [entities, element, id],
    )

    function remove(i: number) {
        setChanged(true)

        let newEntities = entities.slice()
        newEntities.splice(i, 1)
        setEntities(newEntities)

        let entityArr = newEntities.map((e) => ({
            id: e.id,
            dataset: element.entities![e._entityIndex!].dataset,
        }))
        entitiesApi
            .updateMultiMP(id, element.target!, entityArr)
            .catch((err) => setErr(err.message || err))
    }

    return (
        <div className='mt-2'>
            <div className='flex'>
                <label className='font-bold text-blue-700'>
                    {element.label}
                    <ChangeIndicator changed={changed} />
                </label>

                {!!id && (
                    <div className='text-gray-400 text-sm ml-10'>
                        Note: This field is saved as you edit.
                    </div>
                )}
                {!id && (
                    <div className='text-gray-400 text-sm ml-10'>
                        Note: You must save the new row before you can edit this field.
                    </div>
                )}
            </div>
            {err && <div className='text-red-500'>{err}</div>}
            <EntitySearch
                entities={element.entities!}
                text={text}
                textChange={setText}
                onErr={setErr}
                optionClick={optionClick}
                readonly={!id}
            />

            <div className='mt-3 border border-gray-200 rounded px-2 py-2'>
                {!entities.length && <div className='text-gray-500 text-sm'>None Selected</div>}
                {entities.map((e, i) => (
                    <div key={i} className='mb-2'>
                        <Tag key={e.id} color='blue'>
                            {e.name}

                            <span className='text-sm ml-2 opacity-50'>
                                ({element.entities![e._entityIndex!].dataset})
                            </span>

                            <span
                                className='ml-4 hover:text-blue-200 transition cursor-pointer'
                                onClick={() => remove(i)}
                            >
                                <i className='lni lni-cross-circle' />
                            </span>
                        </Tag>
                    </div>
                ))}
            </div>
        </div>
    )
}
